<template>
  <div :class="[{ spinner: isPageLoading }]">
    <pn-spinner size="4" v-show="isPageLoading" />
  </div>
  <pn-toast
    v-if="actionMessage.show"
    closable
    temporary
    :text="actionMessage.text"
    :appearance="actionMessage.success ? 'success' : 'error'"
    class="notification"
    @close="actionMessage.show = false"
  ></pn-toast>
  <div v-if="!isLoadingDetail">
    <div class="page-actions-top">
      <pn-button
        appearance="light"
        variant="outlined"
        small
        @click="router.back()"
      >
        ← Go back
      </pn-button>
    </div>
    <div v-if="error">
      <pn-toast closable="false" appearance="error" :text="error"></pn-toast>
    </div>
    <div v-if="currentOrder">
      <h1>Order #{{ currentOrder.orderId }}</h1>
      <section>
        <dl class="detail-list">
          <dt>Order ID</dt>
          <dd>{{ currentOrder.orderId }}</dd>
          <dt>Cart ID</dt>
          <dd>{{ currentOrder.cartId || "-" }}</dd>
          <dt>Product</dt>
          <dd>{{ currentOrder.name || "-" }}</dd>
          <dt>Address Type</dt>
          <dd>{{ currentOrder.addressType }}</dd>
          <dt>Product ID</dt>
          <dd>{{ currentOrder.productId }}</dd>
          <dt>Print Product code</dt>
          <dd>{{ currentOrder.productCode || "-" }}</dd>
          <dt>User ID</dt>
          <dd>{{ currentOrder.userId || "-" }}</dd>
          <dt>Order Date</dt>
          <dd>{{ utc2local(currentOrder.orderDate) }}</dd>
          <!-- TODO: will check later -->
          <!-- <dt>Upload Date</dt>
          <dd>{{ utc2local(currentOrder.uploadInfo?.uploadDate) || "-" }}</dd> -->
          <!-- TODO: will check later -->
          <dt>Delivery Date</dt>
          <dd>{{ utc2local(currentOrder.deliveryDate) || "-" }}</dd>
          <dt>Status</dt>
          <dd>{{ currentOrder.status }}</dd>
        </dl>
        <div class="action-row">
          <pn-button
            v-if="currentOrder.status === MAILOUT_STATUSES.UNPROCESSED"
            appearance="danger"
            @click="onCancelOrder"
          >
            Cancel Order
          </pn-button>
          <pn-button
            v-else-if="
              ['CANCELLED', 'PLACED', 'STARTED', 'PROCESSED'].includes(
                currentOrder.status,
              )
            "
            @click="onSendToPrinting"
          >
            Send to printing
          </pn-button>
        </div>
      </section>
      <section>
        <h2>Resend receipt</h2>
        <div class="resend-row">
          <pn-input v-model="receiptEmail" label="E-mail address"></pn-input>
          <pn-button @click="onResendReceipt"> Resend </pn-button>
        </div>
      </section>
      <section v-if="currentOrder.urls?.length">
        <h2>Files</h2>
        <ul class="file-list">
          <li v-for="file in currentOrder.urls" :key="file.filename">
            <div class="file-item">
              <a :href="file.url" target="_blank" rel="noopener noreferrer">
                {{ file.filename }}
              </a>
              <pn-icon :symbol="open_in_new" small="true" />
            </div>
            <div class="file-meta">
              Modified: {{ utc2local(file.modified) }}
            </div>
          </li>
        </ul>
      </section>
      <section>
        <h2>{{ MAILOUT_COMMENTS.SECTION_TITLE }}</h2>
        <div class="comment-form flex flex-direction-column gap-16">
          <pn-textarea
            :label="MAILOUT_COMMENTS.INPUT_LABEL"
            :value="commentText"
            rows="4"
            @change="handleCommentChange"
          ></pn-textarea>
          <pn-button
            :disabled="!commentText.trim() || !authEmail || isAddingComment"
            @click="onAddComment"
          >
            {{ MAILOUT_COMMENTS.ADD_BUTTON }}
          </pn-button>
        </div>
        <p v-if="commentsError" class="comments-error" role="alert">
          {{ commentsError }}
        </p>
        <ul v-if="comments.length" class="comment-list">
          <li
            v-for="(comment, commentIndex) in comments"
            :key="`${comment.createdAt ?? comment.createdAt}-${
              comment.email
            }-${commentIndex}`"
            class="comment-item"
          >
            <p class="comment-text">{{ comment.commentText }}</p>
            <p class="comment-meta">
              <span>{{ comment.email }}</span>
              <span v-if="comment.createdAt ?? comment.createdAt">
                {{ getCommentDate(comment) }}
              </span>
            </p>
          </li>
        </ul>
      </section>
      <!--
      <section>
        <h2>Upload material file</h2>
        <pn-file-upload
          label="Upload material file (.PDF, max 4MB)"
          accept=".pdf"
          @change="(e: Event) => onFileSelect(e, 'material')"
        ></pn-file-upload>
      </section>
      <section>
        <h2>Upload address file</h2>
        <pn-file-upload
          label="Upload address file (.txt)"
          accept=".txt"
          @change="(e: Event) => onFileSelect(e, 'address')"
        ></pn-file-upload>
      </section>
      -->
    </div>
  </div>
  <SendToPrintingModal
    :open="showSendToPrintingModal"
    :showSpinner="isSending"
    :orderStatus="currentOrder?.status ?? ''"
    @onClose="showSendToPrintingModal = false"
    @onConfirm="sendToPrinting"
  />
  <CancelOrderModal
    :open="showCancelOrderModal"
    :showSpinner="isCancelling"
    :orderStatus="currentOrder?.status ?? ''"
    @onClose="showCancelOrderModal = false"
    @onConfirm="(reason: string) => cancelOrder(reason)"
  />
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { open_in_new } from "pn-design-assets/pn-assets/icons";
import { useAuthStore } from "@/stores/auth";
import { useMailOutStore } from "@/stores/mailout";
import { utc2local } from "@/utils";
import CONSTANTS from "@/constant";
import SendToPrintingModal from "@/views/modals/SendToPrintingModal.vue";
import CancelOrderModal from "@/views/modals/CancelOrderModal.vue";
import type { MailOutOrderComment } from "@/types/mailout";

const { MAILOUT_STATUSES, MAILOUT_COMMENTS } = CONSTANTS;

const route = useRoute();
const router = useRouter();
const orderId = route.params.id as string;

const authStore = useAuthStore();
const mailOutStore = useMailOutStore();
const { currentOrder, isLoadingDetail, error } = storeToRefs(mailOutStore);
const { comments } = storeToRefs(mailOutStore);
const { email: authEmail } = storeToRefs(authStore);

const receiptEmail = ref("");
const commentText = ref("");
const commentsError = ref("");
const isSending = ref(false);
const isCancelling = ref(false);
const isResending = ref(false);
const isAddingComment = ref(false);
const isPageLoading = computed(
  () => isLoadingDetail.value || isResending.value || isAddingComment.value,
);
const actionMessage = ref({ show: false, text: "", success: true });
const showSendToPrintingModal = ref(false);
const showCancelOrderModal = ref(false);

onMounted(async () => {
  await mailOutStore.fetchOrderDetail(orderId);
  if (mailOutStore.currentOrder?.contactInfo?.email) {
    receiptEmail.value = mailOutStore.currentOrder.contactInfo.email;
  }
  await fetchOrderComments();
});

const showActionResult = (text: string, success: boolean) => {
  actionMessage.value = { show: true, text, success };
  setTimeout(() => {
    actionMessage.value.show = false;
  }, 4000);
};

const onSendToPrinting = () => {
  showSendToPrintingModal.value = true;
};

const sendToPrinting = async () => {
  if (!currentOrder.value) return;
  isSending.value = true;
  try {
    await mailOutStore.sendToPrinting(orderId);
    showSendToPrintingModal.value = false;
    showActionResult("Order sent to printing successfully.", true);
  } catch {
    showSendToPrintingModal.value = false;
    showActionResult(
      "Failed to send order to printing. Please try again.",
      false,
    );
  } finally {
    isSending.value = false;
  }
};

const onCancelOrder = () => {
  showCancelOrderModal.value = true;
};

const cancelOrder = async (reason: string) => {
  isCancelling.value = true;
  try {
    await mailOutStore.cancelOrder(orderId, reason);
    await mailOutStore.fetchOrderDetail(orderId);
    showCancelOrderModal.value = false;
    showActionResult("Order cancelled successfully.", true);
  } catch {
    showCancelOrderModal.value = false;
    showActionResult("Failed to cancel order. Please try again.", false);
  } finally {
    isCancelling.value = false;
  }
};

const onResendReceipt = async () => {
  if (!receiptEmail.value) return;
  isResending.value = true;
  try {
    await mailOutStore.resendReceipt(orderId, receiptEmail.value);
    showActionResult("Receipt resent successfully.", true);
  } catch {
    showActionResult("Failed to resend receipt. Please try again.", false);
  } finally {
    isResending.value = false;
  }
};

const handleCommentChange = (event: Event) => {
  const eventValue = (event.target as HTMLTextAreaElement | null)?.value;
  const eventDetail = (event as CustomEvent<string>).detail;
  commentText.value = eventValue ?? eventDetail ?? "";
};

const fetchOrderComments = async () => {
  commentsError.value = "";
  try {
    await mailOutStore.fetchOrderComments(orderId);
  } catch {
    commentsError.value = MAILOUT_COMMENTS.FETCH_ERROR_MESSAGE;
  }
};

const formatCommentDate = (createdAt: string | number | undefined) => {
  if (createdAt === undefined || createdAt === "") return "";
  const parsedDate =
    typeof createdAt === "number"
      ? new Date(createdAt < 1_000_000_000_000 ? createdAt * 1000 : createdAt)
      : new Date(createdAt);
  if (Number.isNaN(parsedDate.getTime())) return "";
  return new Intl.DateTimeFormat(MAILOUT_COMMENTS.DATE_LOCALE, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(parsedDate);
};

const getCommentDate = (comment: MailOutOrderComment) => {
  return `· ${formatCommentDate(comment.createdAt ?? comment.createdAt)}`;
};

const onAddComment = async () => {
  const trimmedComment = commentText.value.trim();
  if (!trimmedComment || !authEmail.value) return;
  isAddingComment.value = true;
  try {
    await mailOutStore.addOrderComment(orderId, {
      email: authEmail.value,
      comment: trimmedComment,
    });
    commentText.value = "";
    showActionResult(MAILOUT_COMMENTS.SUCCESS_MESSAGE, true);
    await fetchOrderComments();
  } catch {
    showActionResult(MAILOUT_COMMENTS.ERROR_MESSAGE, false);
  } finally {
    isAddingComment.value = false;
  }
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const onFileSelect = (_e: Event, _type: "material" | "address") => {
  // TODO: replace with real file upload API call
  showActionResult("File uploaded successfully.", true);
};
</script>

<style lang="scss" scoped>
.detail-list {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 0.75rem 1.5rem;
  margin-bottom: 2rem;
}

.detail-list dt {
  font-weight: bold;
}

.comment-form {
  max-width: 640px;
}

.comment-form pn-textarea {
  width: 100%;
}

.comment-form pn-button {
  align-self: flex-start;
}

.comment-list {
  list-style: none;
  margin: 1.5rem 0 0;
  max-width: 640px;
  padding: 0;
}

.comment-item {
  border-bottom: 1px solid #d6d1ca;
  padding: 1rem 0;
}

.comment-text {
  margin: 0 0 0.5rem;
  white-space: pre-wrap;
}

.comment-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  color: #666;
  font-size: 1rem;
  margin: 0;
}

.comments-error {
  color: #b00020;
}

.action-row {
  margin-bottom: 2rem;
}

.resend-row {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 2rem;
}

.file-list {
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
}

.file-list li {
  margin-bottom: 1rem;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.file-meta {
  font-size: 0.875rem;
  color: #666;
}

.page-actions-top {
  margin-bottom: 1.5rem;
}

.notification {
  position: fixed;
  bottom: 12px;
  padding: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
}

.spinner {
  width: 100%;
  height: 100%;
  position: fixed;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 99;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
